<template>
    <div class="bg-white border border-[#E3EAF4] p-4 rounded-[12px] text-[14px] md:text-[16px]">
        <div class="mb-4">
            <div class="text-[#2C2C4F] font-weight-bold text-[20px] p-3">{{ offerTitle }}</div>
            <div class="flex justify-between items-center mb-1 pl-3 py-1 pr-0">
                <span>{{ solarPanel.title }}</span>
                <div class="flex items-center rounded-[12px] border-[1px] border-[#959BB]">
                    <button :disabled="minPanelsNumber >= panelsNumber" @click="decrementPanelsNumber" class="focus:outline-none h-10 px-2 disabled:opacity-30">
                        -
                    </button>
                    <span class="text-[#E3EAF4] text-2xl font-thin">|</span>
                    <input type="number" :min="minPanelsNumber" :max="maxPanelsNumber" class="remove-arrow font-weight-bold w-8 text-center focus:outline-none h-10 rounded-none" v-model="panelsNumber" @change="onUpdateOffer($event)">
                    <span class="text-[#E3EAF4] text-2xl font-thin">|</span>
                    <button :disabled="maxPanelsNumber <= panelsNumber" @click="incrementPanelsNumber" class="focus:outline-none h-10 px-2 disabled:opacity-30">
                        +
                    </button>
                </div>
            </div>
            <div class="flex justify-between items-center mb-1 bg-[#E3EAF4]/[.3] rounded-sm p-3">
                <span>{{ inverter.title }}</span>
                <span>1x</span>
            </div>
            <div class="flex justify-between items-center mb-1 p-3">
                <span>{{ roofInstallationLabel }}</span>
                <span>{{ $t('offer_v1.Included') }}</span>
            </div>
            <div class="flex justify-between items-center bg-[#E3EAF4]/[.3] rounded-sm p-3">
                <span>{{ $t('offer_v1.Inspection and administration') }}</span>
                <span>{{ $t('offer_v1.Included') }}</span>
            </div>
            <div class="flex justify-between items-center p-3 font-bold">
                <span>{{ $t('offer_v1.Total solar panels') }}</span>
                <span>{{ panelsPrice }}</span>
            </div>
        </div>

        <div class="border-t pt-2">
            <label for="battery-switch" class="flex justify-between items-center p-3 font-bold cursor-pointer">
                <span class="text-[20px] text-[#2C2C4F]">{{ $t('offer_v1.Add a battery') }}</span>
                <span>
                    <div class="relative">
                        <input type="checkbox" id="battery-switch" class="sr-only" v-model="isBatteryAdded" @change="onUpdateOffer($event)">
                        <div class="block bg-[#E3EAF4] w-14 h-8 rounded-full" :class="{'bg-blue-600': isBatteryAdded}"></div>
                        <div class="dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition" :class="{'translate-x-6': isBatteryAdded}"></div>
                    </div>
                </span>
            </label>
            <div v-if="isBatteryAdded" class="p-3">
                <div class="flex justify-between items-center rounded-sm p-3">
                    <span class="">{{ $t('offer_v1.Battery Capacity') }}</span>
                    <label v-for="battery in batteries" :key="battery.id" :for="`battery-`+battery.id" class="inline-flex items-center cursor-pointer">
                        <input type="radio" class="form-radio" :id="`battery-`+battery.id" v-model="batterySelected" @change="onUpdateOffer($event)" :value="battery.id">
                        <span class="ml-1" :class="{ 'font-bold' : batterySelected === battery.id }">{{ parseInt(battery.kwh) }}kWh</span>
                    </label>
                </div>
                <div class="flex justify-between items-center mb-1 bg-[#E3EAF4]/[.3] p-3">
                    <span>{{ $t('offer_v1.Installation and inspection') }}</span>
                    <span>{{ $t('offer_v1.Included') }}</span>
                </div>
                <div class="flex justify-between items-center rounded-sm p-3">
                    <span>{{ $t('offer_v1.Total battery') }}</span>
                    <span class="font-bold text-[16px]">{{ batteryPrice }}</span>
                </div>
            </div>
            <div class="mb-4">
                <p class="px-4">{{ $t('offer_v1.Increase your self-consumption with a home battery') }}</p>
            </div>
        </div>

        <div class="pt-4 bg-[#E3EAF4]/[.3] rounded-[12px] p-6 flex flex-col items-center">
            <p class="font-bold mb-2 text-center">{{ $t('offer_v1.Final Price') }}: <span class="font-bold">{{ price }} ({{ $t('offer_v1.incl VAT', {percent: vat}) }})</span></p>
            <button
                @click="isPopupVisible = true"
                class="w-full bg-[#0676C2] text-white font-bold text-[18px] px-6 py-4 rounded-[12px] shadow"
            >
                {{ $t('offer_v1.Send my offer by mail') }}
                <span style="vertical-align: -2px;" class="ml-3 text-2xl font-light">&gt;</span>
            </button>
        </div>

        <div v-if="isPopupVisible" class="fixed inset-0 bg-[#141430] bg-opacity-90 flex justify-center items-start pt-10">
                <div class="flex flex-col items-center max-w-[550px]">
                    <div class="bg-white rounded-[12px] shadow-lg">
                        <div class="p-6">
                            <h3 class="text-lg leading-6 text-[20px] font-bold text-[#2C2C4F] mb-2">{{ $t('offer_v1.Get your digital quote') }}</h3>
                            <p class="text-[#2C2C4F] mb-5">{{ $t('offer_v1.Leave your details and we will send') }}</p>
                            <form class="space-y-4">
                                <div>
                                    <label class="font-bold text-[#2C2C4F] block mb-1">{{ $t('offer_v1.Full name') }}</label>
                                    <input type="text" placeholder="" @change="popupErrors.name=null" v-model="popup.name" class="w-full px-4 py-3 border rounded-[12px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                    <div class="text-red text-sm" v-if="popupErrors.name">{{ popupErrors.name }}</div>
                                </div>
                                <div>
                                    <label class="font-bold text-[#2C2C4F] block mb-1">{{ $t('offer_v1.Email') }}</label>
                                    <input type="email" placeholder="" @change="popupErrors.email=null" v-model="popup.email" class="w-full px-4 py-3 border rounded-[12px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                    <div class="text-red text-sm" v-if="popupErrors.email">{{ popupErrors.email }}</div>
                                </div>
                                <div>
                                    <label class="font-bold text-[#2C2C4F] block mb-1">{{ $t('offer_v1.Phone number') }}</label>
                                    <input type="tel" placeholder="" @change="popupErrors.phone=null" v-model="popup.phone" class="w-full px-4 py-3 border rounded-[12px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                    <div class="text-red text-sm" v-if="popupErrors.phone">{{ popupErrors.phone }}</div>
                                </div>
                                <div>
                                    <label class="font-bold text-[#2C2C4F] block mb-1">{{ $t('offer_v1.Address') }} <span class="font-normal text-[14px]">({{ $t('offer_v1.we will send you full simulation') }})</span></label>
                                    <input type="text" placeholder="" @change="popupErrors.address=null" v-model="popup.address" class="w-full px-4 py-3 border rounded-[12px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                    <div class="text-red text-sm" v-if="popupErrors.address">{{ popupErrors.address }}</div>
                                </div>
                                <button @click="sendOffer" type="button" class="w-full bg-[#0676C2] text-white font-bold text-[18px] px-6 py-4 rounded-[12px] shadow">
                                    {{ $t('offer_v1.Send my offer by mail') }}
                                    <span style="vertical-align: -2px;" class="ml-3 text-2xl font-light">&gt;</span>
                                </button>
                            </form>
                        </div>
                    </div>
                    <button type="button" class="mt-2 text-white bg-transparent outline-none focus:outline-none" @click="isPopupVisible = false">{{ $t('offer_v1.Close window') }}</button>
                </div>
        </div>
    </div>
    <div class="bg-white border border-[#E3EAF4] p-4 rounded-[12px] font-[16px] mt-4">
        <div class="text-[#2C2C4F] font-weight-bold text-[20px] p-3">{{ $t('offer_v1.Your estimated production') }}</div>

        <div class="px-3 pb-3">
            {{ $t('offer_v1.We estimate that your installation will produce') }} <strong>{{ installationSize }} {{ $t('offer_v1.per year') }}</strong>
            {{ $t('offer_v1.which will results in yearly savings of') }} <strong>{{ saving_annual }}</strong>.
        </div>

        <div class="p-3 w-full">
            <img src="/img/offers_calc/BE_graph.svg" class="w-full h-auto" alt="Graph" />
        </div>

        <div class="p-6 mb-3 max-w-md mx-auto rounded-[12px] bg-[#C5E7FF] flex items-start">
            <img src="/img/icons/icon_info.svg" class="w-[22px] h-[21px] mt-[2px]" alt="Info icon" />
            <div class="flex-grow text-xs text-[#005893] ml-4">
                {{ $t('offer_v1.savings_text') }}
            </div>
        </div>

        <div class="p-3">
            <button @click="isPopupVisible = true" class="w-full bg-[#0676C2] text-white font-bold text-[18px] px-6 py-4 rounded-[12px] shadow">
                {{ $t('offer_v1.savings_button') }}
                <span style="vertical-align: -2px;" class="ml-3 text-2xl font-light">&gt;</span>
            </button>
        </div>
    </div>
</template>

<script>

export default {
    name: "SingleOffer",
    props: {
        calculatorId: {
            type: String,
            required: true,
        },
        offer: {
            type: Object,
            required: true,
        },
        allSolarPanels: {
            type: Array,
            required: true,
        },
    },
    components: {
    },
    data() {
        return {
            solarPanelTitle: null,
            solarPanelSelected: null,
            panelsNumber: null,
            inverterTitle: null,
            inverterSelected: null,
            panelsPrice: '',
            batteryPrice: '',
            price: '',
            vat: '',
            installationSize: 'kWh',
            suitableInverters: [],
            isBatteryAdded: false,
            isPopupVisible: true,
            minPanelsNumber: 1,
            maxPanelsNumber: 1,
            batteries: [],
            batterySelected: null,
            saving_annual: '',
            offerTitle: '',
            roofInstallationLabel: '',
            popup: {
                name: '',
                email: '',
                phone: '',
                address: '',
            },
            popupErrors: {
                name: null,
                email: null,
                phone: null,
                address: null,
            }
        };
    },
    mounted() {
        this.setDataToDisplay(this.offer);
    },
    computed: {
        solarPanel() {
            if (this.solarPanelTitle) {
                return {
                    title: this.solarPanelTitle,
                }
            }
            let solarPanel = this.allSolarPanels.find((solarPanel) => solarPanel.id === this.solarPanelSelected);
            if (solarPanel) {
                return solarPanel;
            } else {
                return {
                    title: '...',
                }
            }
        },
        inverter() {
            if (this.inverterTitle) {
                return {
                    title: this.inverterTitle,
                }
            }
            let inverter = this.suitableInverters.find((inverter) => inverter.id === this.inverterSelected);
            if (inverter) {
                return inverter;
            } else {
                return {
                    title: '...',
                }
            }
        },

    },
    methods: {
        incrementPanelsNumber() {
            this.panelsNumber += 1;
            this.onUpdateOffer();
        },
        decrementPanelsNumber() {
            if (this.panelsNumber > 1) {
                this.panelsNumber -= 1;
                this.onUpdateOffer();
            }
        },
        setDataToDisplay(data) {
            this.solarPanelTitle = data.solar_panel_title;
            this.solarPanelSelected = data.solar_panel.id;
            this.panelsNumber = data.panels_number;
            this.minPanelsNumber = data.min_panels_number;
            this.maxPanelsNumber = data.max_panels_number;
            this.inverterTitle = data.inverter_title;
            this.inverterSelected = data.inverter.id;
            this.panelsPrice = data.prices['panels_system_price_formatted'];
            this.batteryPrice = data.prices['battery_price_formatted'];
            this.price = data.prices['price_vat_formatted'];
            this.vat = data.prices['vat'].toString();
            this.suitableInverters = data.suitable_inverters;
            this.batteries = data.batteries;
            this.isBatteryAdded = (data.battery_id !== null);
            this.batterySelected = data.battery_id ? data.battery_id : null;
            this.installationSize = data.installation_size_formatted;
            this.saving_annual = data.savings['annual_formatted'];
            this.offerTitle = data.title;
            this.roofInstallationLabel = data.roof_installation_label;
        },
        onUpdateOffer(event) {
            if (this.panelsNumber < this.minPanelsNumber) {
                this.panelsNumber = this.minPanelsNumber;
            }

            if (this.panelsNumber > this.maxPanelsNumber) {
                this.panelsNumber = this.maxPanelsNumber;
            }

            if (this.isBatteryAdded) {
                if (this.batterySelected === null) {
                    if (this.batteries.length > 0) {
                        this.batterySelected = this.batteries[0].id;
                    }
                }
            } else {
                this.batterySelected = null;
            }

            axios.post(
                this.route("api.calculator-update-offer"),
                {
                    calculator_id: this.calculatorId,
                    offer_type: this.offer.offer_type,
                    // solar_panel_id: this.solarPanelSelected,
                    panels_number: this.panelsNumber,
                    // inverter_id: this.inverterSelected,
                    battery_id: this.batterySelected,
                }
            ).then((response) => {
                if (response.data.offer) {
                    this.setDataToDisplay(response.data.offer);
                } else {
                    console.error('Not offer');
                }
            });
        },
        sendOffer() {
            axios.post(
                this.route("api.calculator-send-offer"),
                {
                    calculator_id: this.calculatorId,
                    offer_types: [this.offer.offer_type],
                    name: this.popup.name,
                    email: this.popup.email,
                    phone: this.popup.phone,
                    address: this.popup.address,
                }
            ).then((response) => {
                if (response.data.success) {
                    this.isPopupVisible = false;
                    this.$toast.success(this.$t('offer_v1.Offer sent successfully', {duration: 5000}));
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: 'api-calc-lead',
                        conversion_id: response.data.calculator_id,
                        conversion_value: 1
                    });
                } else {
                    if (response.data.errors && (Object.keys(response.data.errors).length > 0)) {
                        Object.entries(response.data.errors).forEach(([field, errorText]) => {
                            this.popupErrors[field] = errorText;
                        });
                    } else {
                        this.$toast.error(this.$t('offer_v1.Error while sending offer'));
                    }
                }
            }).catch((error) => {
                console.error(error);
                this.$toast.error(this.$t('offer_v1.Error while sending offer', {duration: 10000}));
            })
        }
    },
};
</script>

<style scoped>
.normal-select {
    appearance: auto;
}
.remove-arrow::-webkit-inner-spin-button,
.remove-arrow::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.remove-arrow {
    -moz-appearance: textfield;
}
</style>
